import Phaser from 'phaser';
import { PALETTE } from '../config';
import { AudioEngine } from '../game/AudioEngine';

export interface ButtonOptions {
  width?: number;
  height?: number;
  color?: number;
  textColor?: string;
  fontSize?: number;
  /** Optional crisp vector glyph drawn left of the label (no emoji). */
  icon?: 'play';
}

/**
 * Premium pill button: layered fills fake a soft vertical gradient with a glossy top sheen,
 * a coloured outer glow and a crisp rim, so it reads like AAA mobile UI (no flat emoji icons).
 * Enforces mobile-friendly minimums (tap height and font never drop below readable thresholds).
 */
export class Button extends Phaser.GameObjects.Container {
  private bg: Phaser.GameObjects.Graphics;
  private label: Phaser.GameObjects.Text;
  private readonly boxW: number;
  private readonly boxH: number;
  private readonly baseColor: number;

  constructor(scene: Phaser.Scene, x: number, y: number, text: string, onTap: () => void, opts: ButtonOptions = {}) {
    super(scene, x, y);
    this.boxW = Math.max(180, opts.width ?? 320);
    this.boxH = Math.max(58, opts.height ?? 72);
    this.baseColor = opts.color ?? PALETTE.laneAccent;
    const fontPx = Math.round(Math.min(40, Math.max(20, opts.fontSize ?? this.boxH * 0.42)));

    this.bg = scene.add.graphics();
    this.label = scene.add
      .text(0, 0, text, {
        fontFamily: '"Twemoji Country Flags", system-ui, -apple-system, sans-serif',
        fontSize: `${fontPx}px`,
        color: opts.textColor ?? '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setShadow(0, 2, '#00000077', 4);
    this.add([this.bg, this.label]);
    this.draw(this.baseColor);

    // Optional vector "play" glyph: lay [glyph · gap · label] out as one centred group so the
    // glyph always sits INSIDE the button, whatever the label width.
    if (opts.icon === 'play') {
      const rad = this.boxH * 0.2;
      const gap = rad * 0.8;
      const groupW = rad * 2 + gap + this.label.width;
      const startX = -groupW / 2;
      const px = startX + rad;
      const g = scene.add.graphics();
      g.fillStyle(0xffffff, 0.18).fillCircle(px, 0, rad);
      const t = rad * 0.6;
      g.fillStyle(0xffffff, 1).fillTriangle(px - t * 0.45, -t, px - t * 0.45, t, px + t * 0.95, 0);
      this.add(g);
      this.label.setX(startX + rad * 2 + gap + this.label.width / 2);
    }

    this.setSize(this.boxW, this.boxH);
    this.setInteractive({ useHandCursor: true });
    this.on(Phaser.Input.Events.POINTER_DOWN, () => {
      AudioEngine.resume();
      AudioEngine.tap(0.4);
      scene.tweens.add({ targets: this, scaleX: 0.95, scaleY: 0.95, duration: 70, ease: 'Quad.out' });
    });
    const up = () => scene.tweens.add({ targets: this, scaleX: 1, scaleY: 1, duration: 130, ease: 'Back.out' });
    this.on(Phaser.Input.Events.POINTER_UP, () => {
      up();
      onTap();
    });
    this.on(Phaser.Input.Events.POINTER_OUT, up);

    scene.add.existing(this);
  }

  setText(text: string): this {
    this.label.setText(text);
    return this;
  }

  private draw(color: number): void {
    const c = Phaser.Display.Color.IntegerToColor(color);
    const w = this.boxW;
    const h = this.boxH;
    // Moderate rounding (NOT a full pill): the outer glow/shadow are slightly larger rects, and a
    // pill body (r = h/2) let their flatter corners poke past the rounded ends. A rounded-rect body
    // (r well below h/2) keeps every layer concentric, so the glow is an even halo with no corner.
    const r = Math.min(h * 0.32, 16);
    const g = this.bg;
    g.clear();
    // Soft coloured outer glow.
    g.fillStyle(color, 0.22).fillRoundedRect(-w / 2 - 5, -h / 2 - 4, w + 10, h + 12, r + 5);
    // Drop shadow.
    g.fillStyle(0x000000, 0.34).fillRoundedRect(-w / 2, -h / 2 + 9, w, h, r);
    // Darker 3D lip under the body.
    g.fillStyle(c.clone().darken(30).color, 1).fillRoundedRect(-w / 2, -h / 2 + 5, w, h, r);
    // Body — faux vertical gradient: deep base, then a lighter upper band.
    g.fillStyle(c.clone().darken(6).color, 1).fillRoundedRect(-w / 2, -h / 2, w, h, r);
    g.fillStyle(c.clone().lighten(10).color, 1).fillRoundedRect(-w / 2, -h / 2, w, h * 0.58, r);
    // Glossy top sheen.
    g.fillStyle(c.clone().lighten(34).color, 0.55).fillRoundedRect(-w / 2 + 6, -h / 2 + 4, w - 12, h * 0.36, r * 0.7);
    // Bright top hairline + crisp rim.
    g.lineStyle(2, c.clone().lighten(46).color, 0.9).strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    g.lineStyle(2, 0xffffff, 0.28).beginPath();
    g.moveTo(-w / 2 + r, -h / 2 + 1.5);
    g.lineTo(w / 2 - r, -h / 2 + 1.5);
    g.strokePath();
  }
}
